/**
 * Patient-facing connection status, derived from signals the exercise page
 * already has. Pure functions only — no timers, no network, no store access —
 * so every state can be unit-tested and nothing here can influence the
 * connections it describes. The timing (how long something has been down) is
 * measured by use-connection-status and passed in as plain durations.
 *
 * Five independent subjects, never merged into one "offline" verdict:
 *   network  — the patient's own internet, measured (use-network-quality)
 *   exercise — the scoring WebSocket (use-websocket-connection)
 *   voice    — the realtime voice agent (services/realtime/store)
 *   video    — the reference <video> (components/home/videos-section)
 *   camera   — the local camera stream (exercise reducer + runtime display)
 *
 * None of the other signals says WHY a connection dropped, so their copy never
 * names a cause. Only the network subject may say "your internet is weak",
 * because only it is backed by a measurement of that internet.
 */

import { NETWORK_STATE } from '../../services/network-quality/quality';

// A drop shorter than this is never shown. The socket's own backoff retries at
// 0.5s, 1s and 2s, so a blip normally reopens within ~3.5s plus the connect
// itself — before the patient would notice the counter stall.
export const EXERCISE_ISSUE_DELAY_MS = 4000;
// The socket reconnects forever (there is no max-attempts), so "could not
// connect" is a duration, not an event: this long down while still retrying.
export const EXERCISE_FAILURE_MS = 30000;
// How long "Connection restored" stays up after an interruption the patient saw.
export const RESTORED_VISIBLE_MS = 4000;
// Voice retries wait 1+2+4+8+8s between five attempts, and each attempt is a
// token mint plus a WebRTC handshake (a few seconds on a slow link), so the
// store realistically gives up 35-45s in. It exposes no "gave up" flag, so
// unavailability is a duration: calling it earlier would announce "unavailable"
// while a retry is still likely to succeed.
export const VOICE_ISSUE_DELAY_MS = 5000;
export const VOICE_UNAVAILABLE_MS = 45000;
// Short stalls — the loop restarting, a phone switching cell — are routine and
// mostly clear on their own within a couple of seconds.
export const VIDEO_LOADING_DELAY_MS = 3000;

export const EXERCISE_STATE = Object.freeze({
  connecting: 'connecting',
  connected: 'connected',
  reconnecting: 'reconnecting',
  restored: 'restored',
  failed: 'failed',
});

export const VOICE_STATE = Object.freeze({
  connecting: 'connecting',
  connected: 'connected',
  reconnecting: 'reconnecting',
  unavailable: 'unavailable',
});

export const VIDEO_STATE = Object.freeze({
  playing: 'playing',
  loading: 'loading',
  error: 'error',
});

export const CAMERA_STATE = Object.freeze({
  working: 'working',
  restarting: 'restarting',
  unavailable: 'unavailable',
});

export { NETWORK_STATE };

export const SEVERITY = Object.freeze({
  ok: 'ok',
  warning: 'warning',
  problem: 'problem',
});

/**
 * @param {object} input
 * @param {boolean} input.isOpen          socket readyState is OPEN
 * @param {boolean} input.hasEverOpened   a socket has opened on this page before
 * @param {number|null} input.downForMs   how long the socket has been not-open
 * @param {number|null} input.restoredForMs time since it reopened after a shown issue
 */
export function deriveExerciseState({ isOpen, hasEverOpened, downForMs, restoredForMs }) {
  if (isOpen) {
    return typeof restoredForMs === 'number' && restoredForMs < RESTORED_VISIBLE_MS
      ? EXERCISE_STATE.restored
      : EXERCISE_STATE.connected;
  }

  const down = typeof downForMs === 'number' ? downForMs : 0;

  if (down >= EXERCISE_FAILURE_MS) {
    return EXERCISE_STATE.failed;
  }

  // Before the first open this is the page getting ready (model, camera check,
  // frame geometry), not a lost connection — only a very long wait is news.
  if (!hasEverOpened) {
    return EXERCISE_STATE.connecting;
  }

  return down >= EXERCISE_ISSUE_DELAY_MS ? EXERCISE_STATE.reconnecting : EXERCISE_STATE.connecting;
}

/**
 * One step of the socket timeline the hook keeps: when the current drop began,
 * whether a socket has ever opened, and when a drop the patient was shown
 * ended (the only kind that earns a "restored" confirmation).
 */
export const initialSocketTrack = at => ({ hasEverOpened: false, downSince: at, restoredAt: null });

export function nextSocketTrack(prev, { isOpen, at, issueShown }) {
  if (isOpen) {
    return { hasEverOpened: true, downSince: null, restoredAt: issueShown ? at : null };
  }

  return prev.downSince !== null ? prev : { ...prev, downSince: at, restoredAt: null };
}

/** The banner shows only these; everything else is the details panel's business. */
export function isExerciseBannerState(state) {
  return (
    state === EXERCISE_STATE.reconnecting ||
    state === EXERCISE_STATE.restored ||
    state === EXERCISE_STATE.failed
  );
}

/**
 * @param {object} input
 * @param {boolean} input.enabled        voice guidance is on for this session
 * @param {boolean} input.isOpen         realtime store status is OPEN
 * @param {boolean} input.isIdle         realtime store never started (no session id)
 * @param {number|null} input.troubleForMs time since the first error after the last open
 * @returns {string|null} null when voice is not part of this session
 */
export function deriveVoiceState({ enabled, isOpen, isIdle, troubleForMs }) {
  if (!enabled) {
    return null;
  }

  if (isOpen) {
    return VOICE_STATE.connected;
  }

  if (typeof troubleForMs !== 'number') {
    // Never started (no session id to connect with): nothing the patient can
    // act on, and not a failure of anything they are using.
    return isIdle ? null : VOICE_STATE.connecting;
  }

  if (troubleForMs >= VOICE_UNAVAILABLE_MS) {
    return VOICE_STATE.unavailable;
  }

  return troubleForMs >= VOICE_ISSUE_DELAY_MS ? VOICE_STATE.reconnecting : VOICE_STATE.connecting;
}

/**
 * @param {object} input
 * @param {boolean} input.hasSource      there is a reference video URL at all
 * @param {boolean} input.hasError       the <video> fired `error` since its last load
 * @param {number|null} input.waitingForMs time since `waiting` with no `playing` after it
 */
export function deriveVideoState({ hasSource, hasError, waitingForMs }) {
  if (!hasSource) {
    return null;
  }

  if (hasError) {
    return VIDEO_STATE.error;
  }

  return typeof waitingForMs === 'number' && waitingForMs >= VIDEO_LOADING_DELAY_MS
    ? VIDEO_STATE.loading
    : VIDEO_STATE.playing;
}

/**
 * @param {object} input
 * @param {boolean} input.usesCamera     this exercise is tracked with the camera
 * @param {boolean} input.isReacquiring  a lost stream is being reopened
 * @param {boolean} input.isMissing      motion capture expected, no live camera
 */
export function deriveCameraState({ usesCamera, isReacquiring, isMissing }) {
  if (isReacquiring) {
    return CAMERA_STATE.restarting;
  }

  if (isMissing) {
    return CAMERA_STATE.unavailable;
  }

  return usesCamera ? CAMERA_STATE.working : null;
}

const PROBLEM_STATES = new Set([
  NETWORK_STATE.offline,
  EXERCISE_STATE.failed,
  VOICE_STATE.unavailable,
  VIDEO_STATE.error,
  CAMERA_STATE.unavailable,
]);

const WARNING_STATES = new Set([
  NETWORK_STATE.weak,
  EXERCISE_STATE.reconnecting,
  VOICE_STATE.reconnecting,
  VIDEO_STATE.loading,
  CAMERA_STATE.restarting,
]);

/** Drives the dot on the details button. */
export function summarizeSeverity(states) {
  const list = Object.values(states).filter(Boolean);

  if (list.some(state => PROBLEM_STATES.has(state))) {
    return SEVERITY.problem;
  }

  return list.some(state => WARNING_STATES.has(state)) ? SEVERITY.warning : SEVERITY.ok;
}
